import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Item,
  ItemCreateRequest,
  ItemDetailsFetchRequest,
  ItemsNewFetchRequest,
  ItemUpdateRequest,
} from "../../types";
import {
  getItemDetails,
  getItemsNearby,
  createItem,
  deleteItem,
  updateItem,
  updateItemListingStatus,
  getItemsNewListing,
  getItemsByUserId,
} from "../../services/itemService";
import { RootState } from "../store";
import { resolveLocation } from "../../utils/locationHelper";
import { ERROR_MESSAGES } from "../../utils/constants";
import { extractApiErrorMessage } from "../../helpers/apiHelper";
import { getUserIdFromContext, getLocationFromContext } from "../../utils/apiUtils";

export const fetchItemDetails = createAsyncThunk(
  "item/fetchItemDetails",
  async (
    data: ItemDetailsFetchRequest,
    { getState, rejectWithValue }
  ) => {
    const { itemId, shareableLink = false, lat, lon } = data;
    const locationState = getLocationFromContext(getState() as RootState);
    const resolved = resolveLocation({
      lat,
      lon,
      locationState,
      allowNull: true,
    });
    if (resolved.error) {
      return rejectWithValue(resolved.error);
    }
    const response = await getItemDetails(itemId, {
      shareableLink,
      lat: resolved.lat,
      lon: resolved.lon,
    });
    return response;
  }
);

export const fetchItemsNewListing = createAsyncThunk(
  "item/fetchItemsNewListing",
  async (payload: ItemsNewFetchRequest, { getState }) => {
    const userId = getUserIdFromContext(getState() as RootState);
    const response = await getItemsNewListing(payload, userId);
    return response;
  }
);

export const fetchItemsNearby = createAsyncThunk(
  "item/fetchItemsNearby",
  async ({ lat, lon }: { lat?: number; lon?: number }, { getState, rejectWithValue }) => {
    const userId = getUserIdFromContext(getState() as RootState);
    const locationState = getLocationFromContext(getState() as RootState);
    const resolved = resolveLocation({
      lat,
      lon,
      locationState,
      allowNull: false,
      errorMessage: ERROR_MESSAGES.LOCATION_DATA_REQUIRED,
    });
    if (resolved.error) {
      return rejectWithValue(resolved.error);
    }
    const response = await getItemsNearby(resolved.lat!, resolved.lon!, userId);
    return response;
  }
);

export const fetchItemsByUserId = createAsyncThunk(
  "item/fetchItemsByUserId",
  async (userId: string, { getState }) => {
    const viewerId = getUserIdFromContext(getState() as RootState);
    const locationState = getLocationFromContext(getState() as RootState);
    const resolved = resolveLocation({
      locationState,
      allowNull: true,
    });
    const response = await getItemsByUserId(
      userId,
      viewerId,
      resolved.lat,
      resolved.lon
    );
    return { userId, data: response };
  },
  {
    condition: (userId, { getState }) => {
      const state = getState() as { item: ItemState };
      const { itemsByUserId, lastFetchedUserId, loading } = state.item;

      if (lastFetchedUserId === userId && itemsByUserId !== null && !loading) {
        return false;
      }
      return true;
    },
  }
);

export const createProductItem = createAsyncThunk(
  "item/createProductItem",
  async (
    { item, images }: { item: ItemCreateRequest; images?: File[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await createItem(item, images);
      const responseAny = response as any;
      if (
        responseAny &&
        typeof responseAny === "object" &&
        "data" in responseAny &&
        responseAny.data
      ) {
        return responseAny.data as Item;
      }
      return responseAny as Item;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_CREATE_LISTING)
      );
    }
  }
);

export const deleteProductItem = createAsyncThunk(
  "item/deleteProductItem",
  async (itemId: string) => {
    const responseData = await deleteItem(itemId);
    if (responseData) {
      return responseData;
    }
    return null;
  }
);

export const updateProductItem = createAsyncThunk(
  "item/updateProductItem",
  async (
    { itemId, data, images }: ItemUpdateRequest,
    { rejectWithValue }
  ) => {
    try {
      const responseData = await updateItem({ itemId, data, images });
      if (responseData) {
        return responseData;
      }
      return null;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_UPDATE_LISTING)
      );
    }
  }
);

export const updateProductItemListingStatus = createAsyncThunk(
  "item/updateProductItemListingStatus",
  async ({ itemId, status }: { itemId: string; status: string }) => {
    const responseData = await updateItemListingStatus(itemId, status);
    if (responseData) {
      return responseData;
    }
  }
);
interface ItemState {
  item: Item | null;
  items: Item[] | null;
  itemsNearby: Item[] | null;
  itemsNewListing: Item[] | null;
  itemsByUserId: Item[] | null;
  lastFetchedUserId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: ItemState = {
  item: null,
  items: null,
  itemsNearby: null,
  itemsNewListing: null,
  itemsByUserId: null,
  lastFetchedUserId: null,
  loading: false,
  error: null,
};

const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    setItem: (state, action: PayloadAction<Item>) => {
      state.item = action.payload;
    },
    setItems: (state, action: PayloadAction<Item[]>) => {
      state.items = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearUserItemsCache: (state) => {
      state.itemsByUserId = null;
      state.lastFetchedUserId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItemDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemDetails.fulfilled, (state, action) => {
        state.item = action.payload as Item;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchItemDetails.rejected, (state, action) => {
        state.loading = false;
        state.item = null;
        state.error = action.payload as string;
      })
      .addCase(fetchItemsNearby.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemsNearby.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.itemsNearby = Array.isArray(payload) ? payload : null;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchItemsNearby.rejected, (state, action) => {
        state.loading = false;
        state.itemsNearby = null;
        state.error = action.payload as string;
      })
      .addCase(fetchItemsNewListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemsNewListing.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.itemsNewListing = Array.isArray(payload) ? payload : null;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchItemsNewListing.rejected, (state, action) => {
        state.loading = false;
        state.itemsNewListing = null;
        state.error = action.payload as string;
      })
      .addCase(fetchItemsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemsByUserId.fulfilled, (state, action) => {
        const { userId, data } = action.payload as { userId: string; data: any };
        const payload = data;
        state.itemsByUserId = Array.isArray(payload) ? payload : null;
        state.lastFetchedUserId = userId;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchItemsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.itemsByUserId = null;
        state.lastFetchedUserId = null;
        state.error = action.payload as string;
      })
      .addCase(createProductItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createProductItem.fulfilled,
        (state, action: PayloadAction<Item | null>) => {
          state.item = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(createProductItem.rejected, (state, action) => {
        state.loading = false;
        state.item = null;
        state.error = action.payload as string;
      })
      .addCase(deleteProductItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProductItem.fulfilled, (state, action) => {
        if (state.itemsByUserId && Array.isArray(state.itemsByUserId)) {
          state.itemsByUserId = state.itemsByUserId.filter(
            (item) => item.itemId !== action.meta.arg
          );
        }
        state.item = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteProductItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProductItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductItem.fulfilled, (state, action) => {
        state.item = action.payload?.data as Item;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateProductItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setItem, setItems, setLoading, setError, clearUserItemsCache } = itemSlice.actions;
export const itemSelector = (state: RootState) => state.item;
export default itemSlice.reducer;
